import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiPlus, FiTrash2, FiEdit2, FiMapPin, FiUsers, FiX, FiBuilding, FiMail, FiPhone, FiInfo, FiGlobe, FiCopy, FiExternalLink, FiCheck, FiLock } = FiIcons;

const DepartmentManagement = () => {
  const { user } = useAuth();
  const { companies, departments = [], addDepartment, updateDepartment, deleteDepartment } = useData();
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const company = companies.find(c => c.id === user?.companyId);
  const isKonsern = company?.subscription === 'konsern' || company?.subscription === 'offentlig-kommunal' || company?.departments_count > 1;

  // Filtrer avdelinger
  const companyDepts = Array.isArray(departments) ? departments.filter(d => d.companyId === user?.companyId || d.company_id === user?.companyId) : [];

  // Beregn grenser
  const maxDepartments = company?.departments_count || (company?.subscription === 'konsern' ? 50 : 1);
  const usedSlots = companyDepts.length;
  const slotsAvailable = maxDepartments - usedSlots;
  const canAddMore = slotsAvailable > 0;

  const [deptForm, setDeptForm] = useState({
    id: '',
    name: '',
    location: '',
    manager_name: '',
    email: '',
    phone: '',
    employee_count: '',
    description: '',
    use_own_portal: false,
    branding: { primaryColor: '#1e40af', logoUrl: '' }
  });

  const handleOpenAdd = () => {
    if (!canAddMore) return;
    setIsEditing(false);
    setDeptForm({
      id: crypto.randomUUID(),
      name: '',
      location: '',
      manager_name: '',
      email: '',
      phone: '',
      employee_count: '',
      description: '',
      use_own_portal: false,
      branding: { primaryColor: '#1e40af', logoUrl: '' }
    });
    setShowModal(true);
  };

  const handleOpenEdit = (dept) => {
    setIsEditing(true);
    setDeptForm({
      id: dept.id,
      name: dept.name || '',
      location: dept.location || '',
      manager_name: dept.manager_name || '',
      email: dept.email || '',
      phone: dept.phone || '',
      employee_count: dept.employee_count || '',
      description: dept.description || '',
      use_own_portal: dept.use_own_portal || false,
      branding: dept.branding || { primaryColor: '#1e40af', logoUrl: '' }
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Er du sikker på at du vil slette denne avdelingen permanent?")) {
      try {
        await deleteDepartment(id);
      } catch (error) {
        alert("Kunne ikke slette avdeling: " + error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...deptForm, companyId: user.companyId, active: true };
    try {
      if (isEditing) {
        await updateDepartment(deptForm.id, payload);
      } else {
        await addDepartment(payload);
      }
      setShowModal(false);
    } catch (error) {
      alert("En feil oppstod ved lagring: " + error.message);
    }
  };

  const generateLink = (deptId) => {
    if (!company?.id) return '';
    // VIKTIG: Vi fjerner /#/ for rene BrowserRouter-linker
    return `${window.location.origin}/s/${company.id}?dept=${deptId}`;
  };

  const copyPortalLink = (deptId) => {
    const link = generateLink(deptId);
    navigator.clipboard.writeText(link);
    setCopiedId(deptId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (!isKonsern && maxDepartments <= 1) {
    return (
      <div className="p-8 bg-amber-50 border border-amber-200 rounded-2xl text-amber-800">
        <h3 className="font-bold mb-2 flex items-center gap-2"><SafeIcon icon={FiLock} /> Begrenset tilgang</h3>
        <p>Ditt abonnement ({company?.subscription}) støtter kun én lokasjon. Oppgrader til Konsern for å administrere flere avdelinger.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-900 dark:text-white">Avdelinger & Enheter</h2>
          <p className="text-sm text-gray-500">Administrer unike varslingssider for hver lokasjon.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Kapasitet</p>
            <p className={`font-mono font-bold ${!canAddMore ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
              {usedSlots} / {maxDepartments} brukt
            </p>
          </div>
          <button 
            onClick={handleOpenAdd}
            disabled={!canAddMore}
            className={`text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg transition-all text-sm ${!canAddMore ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {canAddMore ? <><SafeIcon icon={FiPlus} /> Ny avdeling</> : <><SafeIcon icon={FiLock} /> Fullt</>}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {companyDepts.map(dept => (
          <motion.div 
            key={dept.id} 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            className={`p-6 rounded-2xl border shadow-sm flex flex-col ${dept.active ? 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 opacity-75'}`}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl text-blue-600">
                <SafeIcon icon={FiBuilding} className="text-xl" />
              </div>
              <span className={`px-2 py-1 rounded text-[10px] font-black uppercase ${dept.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {dept.active ? 'Aktiv' : 'Inaktiv'}
              </span>
            </div>
            
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">{dept.name}</h3>
            <p className="text-xs text-gray-500 mb-4 flex items-center gap-1"><SafeIcon icon={FiMapPin} /> {dept.location || 'Ingen lokasjon'}</p>

            <div className="mt-auto pt-4 border-t dark:border-gray-700 space-y-3">
              {dept.use_own_portal ? (
                <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-xl border border-purple-100 dark:border-purple-800">
                  <p className="text-[10px] font-black text-purple-600 dark:text-purple-400 uppercase mb-2 flex items-center gap-1">
                    <SafeIcon icon={FiGlobe} /> Avdelingens Microsite
                  </p>
                  <div className="flex items-center gap-2">
                    <input 
                      readOnly 
                      value={generateLink(dept.id)} 
                      className="bg-transparent text-[11px] font-mono text-gray-500 flex-1 outline-none truncate"
                      onFocus={(e) => e.target.select()}
                    />
                    <button 
                      onClick={() => copyPortalLink(dept.id)}
                      className="p-1.5 hover:bg-purple-100 dark:hover:bg-purple-800 rounded text-purple-600 transition-colors"
                      title="Kopier lenke"
                    >
                      <SafeIcon icon={copiedId === dept.id ? FiCheck : FiCopy} />
                    </button>
                    <a 
                      href={generateLink(dept.id)} 
                      target="_blank" 
                      rel="noreferrer"
                      className="p-1.5 hover:bg-purple-100 dark:hover:bg-purple-800 rounded text-purple-600 transition-colors"
                    >
                      <SafeIcon icon={FiExternalLink} />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="p-3 rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                  <p className="text-[10px] text-gray-400 italic flex items-center gap-2">
                    <SafeIcon icon={FiBuilding} /> Bruker felles portal
                  </p>
                </div>
              )}

              <div className="flex justify-between items-center pt-2">
                <div className="flex gap-2">
                  <button onClick={() => handleOpenEdit(dept)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-400 hover:text-blue-600 transition-colors">
                    <SafeIcon icon={FiEdit2} />
                  </button>
                  <button onClick={() => handleDelete(dept.id)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-400 hover:text-red-600 transition-colors">
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
                <div className="text-right">
                  <p className="text-[9px] font-bold text-gray-400 uppercase">Ansatte</p>
                  <p className="text-sm font-black text-gray-700 dark:text-white">{dept.employee_count || 0}</p>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white dark:bg-gray-900 w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b dark:border-gray-800 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-900 z-10">
                <h3 className="text-xl font-black text-gray-900 dark:text-white">{isEditing ? 'Rediger enhet' : 'Opprett ny enhet'}</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-white"><SafeIcon icon={FiX} size={24} /></button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-8 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-black uppercase text-gray-400 mb-1">Navn på avdeling *</label>
                    <input 
                      type="text" required
                      value={deptForm.name}
                      onChange={e => setDeptForm({ ...deptForm, name: e.target.value })}
                      className="w-full p-3 border-2 rounded-xl dark:bg-gray-800 dark:border-gray-700 dark:text-white text-sm focus:border-blue-500 outline-none" 
                      placeholder="F.eks. Salg Oslo"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase text-gray-400 mb-1">Lokasjon *</label>
                    <input 
                      type="text" required
                      value={deptForm.location}
                      onChange={e => setDeptForm({ ...deptForm, location: e.target.value })}
                      className="w-full p-3 border-2 rounded-xl dark:bg-gray-800 dark:border-gray-700 dark:text-white text-sm focus:border-blue-500 outline-none" 
                      placeholder="F.eks. Storgata 1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase text-gray-400 mb-1">Ansvarlig Leder</label>
                    <input 
                      type="text"
                      value={deptForm.manager_name}
                      onChange={e => setDeptForm({ ...deptForm, manager_name: e.target.value })}
                      className="w-full p-3 border-2 rounded-xl dark:bg-gray-800 dark:border-gray-700 dark:text-white text-sm focus:border-blue-500 outline-none" 
                      placeholder="Navn Navnesen"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase text-gray-400 mb-1">Antall Ansatte</label>
                    <input 
                      type="number"
                      value={deptForm.employee_count}
                      onChange={e => setDeptForm({ ...deptForm, employee_count: e.target.value })}
                      className="w-full p-3 border-2 rounded-xl dark:bg-gray-800 dark:border-gray-700 dark:text-white text-sm focus:border-blue-500 outline-none" 
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="bg-[#1e293b] p-6 rounded-2xl border border-gray-700 shadow-inner">
                  <h4 className="font-bold text-white flex items-center gap-2 mb-6">
                    <SafeIcon icon={FiGlobe} /> Varslingsportal og Microsite
                  </h4>
                  
                  <div className="flex items-center gap-4 mb-6">
                    <button 
                      type="button"
                      onClick={() => setDeptForm({ ...deptForm, use_own_portal: false })}
                      className={`flex-1 p-4 rounded-xl border-2 text-sm font-bold transition-all ${!deptForm.use_own_portal ? 'border-gray-500 bg-gray-700 text-white shadow-inner' : 'border-gray-700 text-gray-400 hover:bg-gray-800'}`}
                    >
                      Felles Portal
                    </button>
                    <button 
                      type="button"
                      onClick={() => setDeptForm({ ...deptForm, use_own_portal: true })}
                      className={`flex-1 p-4 rounded-xl border-2 text-sm font-bold transition-all ${deptForm.use_own_portal ? 'border-purple-500 bg-white text-purple-700 shadow-xl' : 'border-gray-700 text-gray-400 hover:bg-gray-800'}`}
                    >
                      Egen Portal
                    </button>
                  </div>

                  {deptForm.use_own_portal && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-top-2">
                       <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">DIN UNIKE LENKE:</p>
                        <code className="text-xs text-blue-400 break-all font-mono">
                          {generateLink(deptForm.id || 'ID')}
                        </code>
                      </div>
                      <div>
                        <label className="block text-xs font-black uppercase text-gray-400 mb-2">LOGO URL</label>
                        <input 
                          type="text"
                          value={deptForm.branding?.logoUrl || ''}
                          onChange={e => setDeptForm({ ...deptForm, branding: { ...deptForm.branding, logoUrl: e.target.value } })}
                          className="w-full p-4 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm focus:ring-2 focus:ring-purple-500 outline-none" 
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-black uppercase text-gray-400 mb-2">HOVEDFARGE</label>
                        <div className="flex items-center gap-4">
                          <input 
                            type="color"
                            value={deptForm.branding?.primaryColor || '#1e40af'}
                            onChange={e => setDeptForm({ ...deptForm, branding: { ...deptForm.branding, primaryColor: e.target.value } })}
                            className="h-12 w-12 rounded-lg cursor-pointer border-0 p-0 bg-transparent" 
                          />
                          <span className="text-gray-400 font-mono text-sm">{deptForm.branding?.primaryColor}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-4 flex justify-end gap-3 border-t dark:border-gray-800">
                  <button type="button" onClick={() => setShowModal(false)} className="px-6 py-3 rounded-xl font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">Avbryt</button>
                  <button type="submit" className="px-8 py-3 bg-blue-600 text-white rounded-xl font-black shadow-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                    <SafeIcon icon={isEditing ? FiEdit2 : FiPlus} />
                    {isEditing ? 'Lagre endringer' : 'Opprett enhet'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DepartmentManagement;